<template>
    <UserLayout :page-title="t('referralCommissions')" :page-section="t('network')">
        <div class="rc-wrap">
            <div class="rc-inner">

                <!-- Welcome Strip -->
                <div class="rc-welcome anim-up" style="--d:0ms">
                    <div class="rcw-left">
                        <div class="rcw-icon">
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="rcw-eyebrow">{{ t('network') }}</p>
                            <h1 class="rcw-title">{{ t('referralCommissions') }}</h1>
                        </div>
                    </div>
                    <div class="rcw-chips">
                        <div class="rcw-chip">
                            <span class="rcw-chip-val">{{ currencySymbol }}{{ formatCurrency(summary.total) }}</span>
                            <span class="rcw-chip-lbl">{{ t('totalCommissions') }}</span>
                        </div>
                        <div class="rcw-chip rcw-chip--green">
                            <span class="rcw-chip-val">{{ currencySymbol }}{{ formatCurrency(summary.paid) }}</span>
                            <span class="rcw-chip-lbl">{{ t('paid') }}</span>
                        </div>
                        <div class="rcw-chip rcw-chip--amber">
                            <span class="rcw-chip-val">{{ currencySymbol }}{{ formatCurrency(summary.pending) }}</span>
                            <span class="rcw-chip-lbl">{{ t('pending') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Stat Cards -->
                <div class="rc-stats">
                    <div class="rc-stat anim-up" style="--d:60ms;--ac:167,93,237">
                        <div class="rcs-glow"></div>
                        <div class="rcs-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                            </svg>
                        </div>
                        <p class="rcs-lbl">{{ t('totalCommissions') }}</p>
                        <p class="rcs-val">{{ currencySymbol }}{{ formatCurrency(summary.total) }}</p>
                        <p class="rcs-sub">{{ t('allTime') }}</p>
                        <div class="rcs-bar-wrap"><div class="rcs-bar shimmer-bar" style="width:100%"></div></div>
                    </div>

                    <div class="rc-stat anim-up" style="--d:100ms;--ac:52,211,153">
                        <div class="rcs-glow"></div>
                        <div class="rcs-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rcs-lbl">{{ t('paidCommissions') }}</p>
                        <p class="rcs-val">{{ currencySymbol }}{{ formatCurrency(summary.approved) }}</p>
                        <p class="rcs-sub">{{ t('received') }}</p>
                        <div class="rcs-bar-wrap"><div class="rcs-bar shimmer-bar" style="width:75%"></div></div>
                    </div>

                    <div class="rc-stat anim-up" style="--d:140ms;--ac:59,130,246">
                        <div class="rcs-glow"></div>
                        <div class="rcs-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                            </svg>
                        </div>
                        <p class="rcs-lbl">{{ t('approvedCommissions') }}</p>
                        <p class="rcs-val">{{ currencySymbol }}{{ formatCurrency(summary.paid) }}</p>
                        <p class="rcs-sub">{{ t('processing') }}</p>
                        <div class="rcs-bar-wrap"><div class="rcs-bar shimmer-bar" style="width:55%"></div></div>
                    </div>

                    <div class="rc-stat anim-up" style="--d:180ms;--ac:251,191,36">
                        <div class="rcs-glow"></div>
                        <div class="rcs-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rcs-lbl">{{ t('pendingCommissions') }}</p>
                        <p class="rcs-val">{{ currencySymbol }}{{ formatCurrency(summary.pending) }}</p>
                        <p class="rcs-sub">{{ t('awaiting') }}</p>
                        <div class="rcs-bar-wrap"><div class="rcs-bar shimmer-bar" style="width:30%"></div></div>
                    </div>
                </div>

                <!-- Main Table Card -->
                <div class="rc-card anim-up" style="--d:240ms">
                    <div class="rc-card-head">
                        <div>
                            <h2 class="rc-card-title">{{ t('commissionHistory') }}</h2>
                            <p class="rc-card-sub">{{ t('allReferralCommissions') }}</p>
                        </div>
                        <div class="rc-filters">
                            <button
                                v-for="filter in ['all', 'paid', 'approved', 'pending']"
                                :key="filter"
                                @click="changeFilter(filter)"
                                class="rc-filter-btn"
                                :class="{ 'rc-filter-btn--active': filterType === filter }">
                                {{ filter === 'all' ? t('all') : formatStatus(filter) }}
                            </button>
                        </div>
                    </div>

                    <!-- Desktop Table -->
                    <div class="rc-table-wrap">
                        <table class="rc-table">
                            <thead>
                            <tr>
                                <th>{{ t('referredUser') }}</th>
                                <th>{{ t('level') }}</th>
                                <th>{{ t('type') }}</th>
                                <th>{{ t('baseAmount') }}</th>
                                <th>{{ t('rate') }}</th>
                                <th>{{ t('commission') }}</th>
                                <th>{{ t('status') }}</th>
                                <th>{{ t('date') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="!filteredCommissions || filteredCommissions.length === 0">
                                <td colspan="8" class="rc-empty">
                                    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                    <p>{{ t('noCommissionsFound') }}</p>
                                    <span>{{ t('noCommissionHistory') }}</span>
                                </td>
                            </tr>
                            <tr v-for="commission in filteredCommissions" :key="commission.id" class="rc-tr">
                                <td>
                                    <div class="rc-member">
                                        <div class="rc-avatar">{{ getInitials(commission.referrer_name) }}</div>
                                        <div>
                                            <p class="rc-member-name">{{ commission.referrer_name }}</p>
                                            <p class="rc-member-email">{{ commission.referrer_email }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span class="rc-badge">{{ t('level') }} {{ commission.level }}</span>
                                </td>
                                <td>
                                    <span class="rc-badge">{{ formatType(commission.type) }}</span>
                                </td>
                                <td>
                                    <span class="rc-text-dim">{{ currencySymbol }}{{ formatCurrency(commission.base_amount) }}</span>
                                </td>
                                <td>
                                    <span class="rc-text-white">{{ commission.commission_rate }}%</span>
                                </td>
                                <td>
                                    <span class="rc-amount">+{{ currencySymbol }}{{ formatCurrency(commission.commission_amount) }}</span>
                                </td>
                                <td>
                                    <span class="rc-status-badge" :style="getStatusStyle(commission.status)">{{ formatStatus(commission.status) }}</span>
                                </td>
                                <td>
                                    <p class="rc-date">{{ formatDate(commission.created_at) }}</p>
                                    <p class="rc-time">{{ formatTime(commission.created_at) }}</p>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Mobile Cards -->
                    <div class="rc-mob-list">
                        <div v-if="!filteredCommissions || filteredCommissions.length === 0" class="rc-mob-empty">
                            <svg width="44" height="44" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <p>{{ t('noCommissionsFound') }}</p>
                        </div>
                        <div v-for="commission in filteredCommissions" :key="commission.id" class="rc-mob-item">
                            <div class="rc-mob-top">
                                <div class="rc-mob-user">
                                    <div class="rc-avatar">{{ getInitials(commission.referrer_name) }}</div>
                                    <div class="rc-mob-info">
                                        <p class="rc-member-name">{{ commission.referrer_name }}</p>
                                        <p class="rc-member-email">{{ commission.referrer_email }}</p>
                                    </div>
                                </div>
                                <span class="rc-status-badge" :style="getStatusStyle(commission.status)">{{ formatStatus(commission.status) }}</span>
                            </div>
                            <div class="rc-mob-grid">
                                <div class="rc-mob-cell">
                                    <span class="rc-mob-lbl">{{ t('commission') }}</span>
                                    <span class="rc-amount">+{{ currencySymbol }}{{ formatCurrency(commission.commission_amount) }}</span>
                                </div>
                                <div class="rc-mob-cell">
                                    <span class="rc-mob-lbl">{{ t('rate') }}</span>
                                    <span class="rc-text-white">{{ commission.commission_rate }}%</span>
                                </div>
                            </div>
                            <div class="rc-mob-foot">
                                <span class="rc-text-dim">{{ t('type') }}: <b>{{ formatType(commission.type) }}</b> &nbsp;·&nbsp; {{ t('level') }}: <b>{{ commission.level }}</b></span>
                                <span class="rc-time">{{ formatDate(commission.created_at) }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="rc-pagination">
                        <p class="rc-page-info">
                            {{ t('showing') }} <b>{{ commissions.from || 0 }}</b> {{ t('to') }} <b>{{ commissions.to || 0 }}</b> {{ t('of') }} <b>{{ commissions.total || 0 }}</b> {{ t('results') }}
                        </p>
                        <div class="rc-page-btns">
                            <Link :href="commissions.prev_page_url" :disabled="!commissions.prev_page_url"
                                  class="rc-page-btn" :class="{ 'rc-page-btn--disabled': !commissions.prev_page_url }" preserve-scroll>
                                {{ t('previous') }}
                            </Link>
                            <Link v-for="pg in visiblePages" :key="pg" :href="getPageUrl(pg)"
                                  class="rc-page-num" :class="{ 'rc-page-num--active': pg === commissions.current_page }" preserve-scroll>
                                {{ pg }}
                            </Link>
                            <Link :href="commissions.next_page_url" :disabled="!commissions.next_page_url"
                                  class="rc-page-btn" :class="{ 'rc-page-btn--disabled': !commissions.next_page_url }" preserve-scroll>
                                {{ t('next') }}
                            </Link>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage, Link} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const props = defineProps({
    commissions: {
        type: Object,
        required: true
    },
    summary: {
        type: Object,
        required: true
    }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const filterType = ref('all');

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return {r: 134, g: 239, b: 172};
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    return rgbToHex(
        Math.min(255, Math.max(0, rgb.r + amount)),
        Math.min(255, Math.max(0, rgb.g + amount)),
        Math.min(255, Math.max(0, rgb.b + amount))
    );
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const filteredCommissions = computed(() => {
    if (filterType.value === 'all') return props.commissions.data || [];
    return (props.commissions.data || []).filter(c => c.status === filterType.value);
});

const visiblePages = computed(() => {
    const pages = [];
    const current = props.commissions.current_page || 1;
    const last = props.commissions.last_page || 1;

    for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || Math.abs(i - current) <= 1) {
            pages.push(i);
        }
    }
    return pages;
});

const getPageUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return url.pathname + url.search;
};

const changeFilter = (filter) => {
    filterType.value = filter;
};

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
};

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        return new Date(dateString).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
    } catch {
        return t('invalidDate');
    }
};

const formatTime = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        return new Date(dateString).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'});
    } catch {
        return t('invalidTime');
    }
};

const formatType = (type) => {
    const typeMap = {
        'deposit': t('deposit'),
        'investment_profit': t('investmentProfit'),
        'reinvestment': t('reinvestment')
    };
    return typeMap[type] || type.replace(/_/g, ' ');
};

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown');
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusStyle = (status) => {
    if (!status) return {
        backgroundColor: 'rgba(148, 163, 184, 0.2)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        color: '#94a3b8'
    };

    switch (status.toLowerCase()) {
        case 'paid':
            return {
                backgroundColor: `rgba(${themeColors.value.primaryRgb}, 0.2)`,
                borderColor: `rgba(${themeColors.value.primaryRgb}, 0.3)`,
                color: themeColors.value.primary
            };
        case 'approved':
            return {
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                borderColor: 'rgba(59, 130, 246, 0.3)',
                color: '#3b82f6'
            };
        case 'pending':
            return {
                backgroundColor: 'rgba(251, 191, 36, 0.2)',
                borderColor: 'rgba(251, 191, 36, 0.3)',
                color: '#fbbf24'
            };
        default:
            return {
                backgroundColor: 'rgba(148, 163, 184, 0.2)', borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#94a3b8'
            };
    }
};

const getInitials = (name) => {
    if (!name) return 'NA';
    const parts = name.split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) showToast(flash.success, 'success');
        if (flash?.error) showToast(flash.error, 'error');
        if (flash?.warning) showToast(flash.warning, 'warning');
        if (flash?.info) showToast(flash.info, 'info');
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.rc-wrap { width:100%; }
.rc-inner { max-width:1400px; margin:0 auto; padding:20px 16px; display:flex; flex-direction:column; gap:14px; }
@media(min-width:768px) { .rc-inner { padding:24px; gap:16px; } }
@media(min-width:1024px) { .rc-inner { padding:28px 32px; } }

/* Welcome */
.rc-welcome { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:16px; padding:16px 20px; display:flex; align-items:center; justify-content:space-between; gap:12px; position:relative; overflow:hidden; flex-wrap:wrap; }
.rc-welcome::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.rcw-left { display:flex; align-items:center; gap:12px; }
.rcw-icon { width:40px; height:40px; border-radius:11px; background:rgba(167,93,237,0.14); border:1px solid rgba(167,93,237,0.26); display:flex; align-items:center; justify-content:center; color:#a75ded; flex-shrink:0; }
.rcw-eyebrow { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.36); margin:0 0 2px; }
.rcw-title { font-size:18px; font-weight:700; color:#fff; margin:0; }
.rcw-chips { display:flex; gap:7px; flex-wrap:wrap; }
.rcw-chip { background:rgba(167,93,237,0.09); border:1px solid rgba(167,93,237,0.17); border-radius:9px; padding:6px 12px; display:flex; flex-direction:column; align-items:center; }
.rcw-chip--green { background:rgba(52,211,153,0.09); border-color:rgba(52,211,153,0.2); }
.rcw-chip--green .rcw-chip-val { color:#34d399; }
.rcw-chip--amber { background:rgba(251,191,36,0.09); border-color:rgba(251,191,36,0.2); }
.rcw-chip--amber .rcw-chip-val { color:#fbbf24; }
.rcw-chip-val { font-size:13px; font-weight:700; color:#a75ded; }
.rcw-chip-lbl { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.38); margin-top:2px; }

/* Stats */
.rc-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; }
@media(min-width:768px) { .rc-stats { grid-template-columns:repeat(4,1fr); } }

.rc-stat { background:rgba(10,4,22,0.97); border:1px solid rgba(var(--ac),0.17); border-radius:16px; padding:14px; position:relative; overflow:hidden; }
.rc-stat::before { content:''; position:absolute; top:0; left:10%; right:10%; height:1px; background:linear-gradient(90deg,transparent,rgba(var(--ac),0.4),transparent); }
.rcs-glow { position:absolute; top:-18px; right:-18px; width:70px; height:70px; border-radius:50%; background:radial-gradient(circle,rgba(var(--ac),0.16) 0%,transparent 70%); pointer-events:none; }
.rcs-icon { width:30px; height:30px; border-radius:8px; background:rgba(var(--ac),0.13); border:1px solid rgba(var(--ac),0.22); display:flex; align-items:center; justify-content:center; color:rgb(var(--ac)); margin-bottom:9px; }
.rcs-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.4); margin:0 0 3px; }
.rcs-val { font-size:20px; font-weight:800; color:#fff; margin:0 0 2px; line-height:1.1; }
.rcs-sub { font-size:11px; font-weight:600; color:rgb(var(--ac)); margin:0 0 8px; }
.rcs-bar-wrap { height:3px; background:rgba(255,255,255,0.06); border-radius:99px; overflow:hidden; }
.rcs-bar { height:3px; border-radius:99px; background:rgba(var(--ac),0.65); }

/* Shimmer */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%); animation:shimmer 2s infinite; }
@keyframes shimmer { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

/* Main Card */
.rc-card { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:16px; overflow:hidden; position:relative; }
.rc-card::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); z-index:1; }

.rc-card-head { padding:18px 20px; border-bottom:1px solid rgba(167,93,237,0.1); display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.rc-card-title { font-size:15px; font-weight:700; color:#fff; margin:0 0 3px; }
.rc-card-sub { font-size:12px; color:rgba(255,255,255,0.4); margin:0; }
.rc-filters { display:flex; gap:6px; flex-wrap:wrap; }
.rc-filter-btn { padding:6px 12px; border-radius:8px; font-size:12px; font-weight:600; border:none; cursor:pointer; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.6); transition:all .2s; }
.rc-filter-btn--active { background:rgba(167,93,237,0.22); color:#a75ded; border:1px solid rgba(167,93,237,0.3); }

/* Desktop Table */
.rc-table-wrap { display:none; overflow-x:auto; }
@media(min-width:900px) { .rc-table-wrap { display:block; } }

.rc-table { width:100%; border-collapse:collapse; }
.rc-table thead { background:rgba(167,93,237,0.06); }
.rc-table th { padding:13px 18px; text-align:left; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.45); white-space:nowrap; }
.rc-tr { border-top:1px solid rgba(167,93,237,0.07); transition:background .15s; }
.rc-tr:hover { background:rgba(167,93,237,0.04); }
.rc-table td { padding:14px 18px; vertical-align:middle; }

.rc-member { display:flex; align-items:center; gap:10px; }
.rc-avatar { width:34px; height:34px; border-radius:9px; background:rgba(167,93,237,0.14); border:1px solid rgba(167,93,237,0.22); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; color:#a75ded; flex-shrink:0; }
.rc-member-name { font-size:13px; font-weight:600; color:#fff; margin:0 0 2px; }
.rc-member-email { font-size:11px; color:rgba(255,255,255,0.38); margin:0; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.rc-badge { display:inline-flex; padding:4px 10px; border-radius:7px; font-size:11px; font-weight:600; background:rgba(167,93,237,0.1); color:rgba(255,255,255,0.65); text-transform:capitalize; }
.rc-amount { font-size:13px; font-weight:700; color:#a75ded; }
.rc-text-white { font-size:13px; font-weight:600; color:#fff; }
.rc-text-dim { font-size:13px; color:rgba(255,255,255,0.5); }
.rc-status-badge { display:inline-flex; padding:4px 10px; border-radius:99px; font-size:11px; font-weight:600; border:1px solid transparent; }
.rc-date { font-size:12px; color:rgba(255,255,255,0.65); margin:0 0 1px; }
.rc-time { font-size:11px; color:rgba(255,255,255,0.35); margin:0; }

.rc-empty { text-align:center; padding:60px 20px; }
.rc-empty svg { color:rgba(167,93,237,0.25); margin:0 auto 12px; display:block; }
.rc-empty p { font-size:15px; font-weight:600; color:rgba(255,255,255,0.5); margin:0 0 4px; }
.rc-empty span { font-size:12px; color:rgba(255,255,255,0.28); }

/* Mobile List */
.rc-mob-list { display:flex; flex-direction:column; }
@media(min-width:900px) { .rc-mob-list { display:none; } }

.rc-mob-empty { text-align:center; padding:50px 20px; }
.rc-mob-empty svg { color:rgba(167,93,237,0.25); margin:0 auto 10px; display:block; }
.rc-mob-empty p { font-size:14px; font-weight:600; color:rgba(255,255,255,0.45); margin:0; }

.rc-mob-item { padding:16px 20px; border-top:1px solid rgba(167,93,237,0.08); display:flex; flex-direction:column; gap:10px; }
.rc-mob-item:hover { background:rgba(167,93,237,0.03); }
.rc-mob-top { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.rc-mob-user { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
.rc-mob-info { flex:1; min-width:0; }
.rc-mob-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.rc-mob-cell { background:rgba(255,255,255,0.03); border:1px solid rgba(167,93,237,0.08); border-radius:9px; padding:10px 12px; }
.rc-mob-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.38); display:block; margin-bottom:4px; }
.rc-mob-foot { display:flex; justify-content:space-between; align-items:center; }
.rc-mob-foot .rc-text-dim { font-size:11px; }
.rc-mob-foot b { color:rgba(255,255,255,0.7); font-weight:600; }

/* Pagination */
.rc-pagination { padding:16px 20px; border-top:1px solid rgba(167,93,237,0.1); display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.rc-page-info { font-size:12px; color:rgba(255,255,255,0.4); }
.rc-page-info b { color:rgba(255,255,255,0.75); font-weight:600; }
.rc-page-btns { display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
.rc-page-btn { padding:7px 14px; border-radius:8px; font-size:12px; font-weight:600; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.65); text-decoration:none; transition:background .2s; }
.rc-page-btn:hover { background:rgba(167,93,237,0.14); color:#a75ded; }
.rc-page-btn--disabled { opacity:.35; pointer-events:none; }
.rc-page-num { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.65); text-decoration:none; transition:all .2s; }
.rc-page-num--active { background:rgba(167,93,237,0.25); color:#a75ded; border:1px solid rgba(167,93,237,0.35); }

@media(prefers-reduced-motion:reduce) {
    .anim-up { animation:none; }
    .shimmer-bar::after { animation:none; }
}
</style>
